import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@material-ui/core';
import Chart from 'react-apexcharts';
import { lodgeColors } from './theme';
import { Panel } from './Panel';
import { hlekweniUrl } from '../../../../utils/constants';

const Stat = ({ l, v, h }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      p: 2.5,
      background: 'linear-gradient(160deg, #FBF7EE 0%, #F2EBD8 100%)'
    }}
  >
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: 11 }}>
      {l}
    </Typography>
    <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 600, mt: 1 }}>
      {v}
    </Typography>
    {h && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {h}
      </Typography>
    )}
  </Paper>
);

const statusOf = (qtyInStock, qtyReceivedTotal) => {
  const ratio = qtyReceivedTotal ? qtyInStock / qtyReceivedTotal : 0;
  if (qtyInStock === 0)
    return { label: 'Out of stock', color: 'error.main', bg: 'rgba(176,57,57,0.12)' };
  if (ratio < 0.25) return { label: 'Reorder', color: 'error.main', bg: 'rgba(176,57,57,0.12)' };
  if (ratio < 0.5) return { label: 'Low', color: 'warning.main', bg: 'rgba(201,138,46,0.18)' };
  return { label: 'Well stocked', color: 'success.main', bg: 'rgba(110,140,75,0.18)' };
};

const HospitalitySatisfactionChart = () => {
  const [cat, setCat] = useState('All');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [tuckData, setTuckData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  useEffect(() => {
    setPage(0);
  }, [cat]);

  useEffect(() => {
    getPosInventory();
    getSalesDetail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const categories = ['All', ...Array.from(new Set(dataList.map((i) => i.category)))];
  const categories = useMemo(
    () => ['All', ...Array.from(new Set(dataList.map((i) => i.category)))],
    [dataList]
  );

  const num = (s) => parseFloat((s || '0').replace(',', '.')) || 0;

  const salesByDay = useMemo(() => {
    const m = new Map();

    tuckData.forEach((s) => {
      const rev = num(s.SellingPrice) * num(s.QtySold);
      m.set(s.ReceiptDate, (m.get(s.ReceiptDate) || 0) + rev);
    });

    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, sales]) => ({ day, sales }));
  }, [tuckData]);

  const categoryMix = useMemo(() => {
    const m = new Map();

    tuckData.forEach((s) => {
      const rev = num(s.SellingPrice) * num(s.QtySold);
      m.set(s.Category, (m.get(s.Category) || 0) + rev);
    });

    return Array.from(m.entries()).map(([category, revenue]) => ({
      category,
      revenue
    }));
  }, [tuckData]);

  const items = cat === 'All' ? dataList : dataList.filter((i) => i.category === cat);
  const pagedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totals = useMemo(() => {
    const revenue = items.reduce((s, i) => s + i.cost * i.qtySoldTotal, 0);
    const units = items.reduce((s, i) => s + i.qtySoldTotal, 0);
    const reorderCount = 0;
    // const reorderCount = items.filter(
    //   (i) => i.qtyReceivedTotal && i.qtyInStock / i.qtyReceivedTotal < 0.25
    // ).length;
    const aov = units ? revenue / units : 0;
    return { revenue, units, reorderCount, aov };
  }, [items]);

  const salesOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    colors: [lodgeColors.primary],
    stroke: { curve: 'smooth', width: 3 },
    dataLabels: { enabled: false },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 100] }
    },
    grid: { borderColor: '#DCD3BD', strokeDashArray: 3 },
    xaxis: {
      categories: salesByDay.map((d) => d.day),
      labels: { style: { colors: '#5A6A5C', fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: { style: { colors: '#5A6A5C', fontSize: '12px' }, formatter: (v) => `R${v}` }
    },
    tooltip: { theme: 'light', y: { formatter: (v) => `$ ${v}` } }
  };
  const salesSeries = [{ name: 'Sales', data: salesByDay.map((d) => +d.sales.toFixed(2)) }];

  const mixOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    colors: [lodgeColors.accent],
    plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '60%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: '#DCD3BD', strokeDashArray: 3 },
    xaxis: {
      categories: categoryMix.map((c) => c.category),
      labels: { style: { colors: '#5A6A5C', fontSize: '12px' }, formatter: (v) => `R${v}` },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: '#5A6A5C', fontSize: '12px' } } },
    tooltip: { theme: 'light', y: { formatter: (v) => `$ ${v}` } }
  };
  const mixSeries = [{ name: 'Revenue', data: categoryMix.map((c) => +c.revenue.toFixed(2)) }];

  const getSalesDetail = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/pos/GetPosSalesDetail?begDate=2025-01-01&endDate=2026-06-01`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned getSalesDetail nn Summary list', result);
          // setSalesData(result.result ?? []);
          setTuckData(result.result);
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  const getPosInventory = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/pos/GetPosInventory`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetPosInventory Summary list', result);
          setDataList(result.result ?? []);
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  /* eslint-disable no-nested-ternary */
  return (
    <Stack spacing={3}>
      <Panel
        title="Tuckshop Performance"
        subtitle="Weekly sales and category mix"
        action={
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="tuck-cat-label">Category</InputLabel>
            <Select
              labelId="tuck-cat-label"
              label="Category"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
            >
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          <Stat l="Revenue (WTD)" v={`$ ${totals.revenue.toLocaleString()}`} h={cat} />
          <Stat l="Units Sold" v={totals.units.toLocaleString()} h="This week" />
          <Stat l="Avg. Item Value" v={`$ ${totals.aov.toFixed(2)}`} h="Per unit sold" />
          <Stat l="Needs Reorder" v={String(totals.reorderCount)} h="At or below reorder point" />
        </Box>
        {/* <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Daily Sales
            </Typography>
            {salesByDay.length > 0 && (
              <Chart options={salesOptions} series={salesSeries} type="area" height={280} />
            )}
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Category Mix
            </Typography>
            {categoryMix.length > 0 && (
              <Chart options={mixOptions} series={mixSeries} type="bar" height={280} />
            )}
          </Box>
        </Box> */}
      </Panel>

      <Panel title="Tuckshop Inventory Levels" subtitle="On hand vs par level with reorder status">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'SKU',
                  'Item',
                  'Category',
                  'Price',
                  'Sold',
                  'Revenue',
                  'On Hand',
                  'Level',
                  'Status'
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontSize: 11,
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedItems.map((i) => {
                const s = statusOf(i.qtyInStock, i.qtyReceivedTotal);
                const pct = i.qtyReceivedTotal
                  ? Math.min(100, (i.qtyInStock / i.qtyReceivedTotal) * 100)
                  : 0;
                const barColor =
                  pct < 25 ? 'error.main' : pct < 50 ? 'warning.main' : 'success.main';
                return (
                  <TableRow key={i.stockNumber} hover>
                    <TableCell
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        color: 'text.secondary'
                      }}
                    >
                      {i.stockNumber}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{i.descriptions}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{i.category}</TableCell>
                    <TableCell>$ {i.cost}</TableCell>
                    <TableCell>{i.qtySoldTotal}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      R {(i.cost * i.qtySoldTotal).toLocaleString()}
                    </TableCell>
                    <TableCell>{i.qtyInStock}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#EFE7D2',
                          '& .MuiLinearProgress-bar': { bgcolor: barColor }
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {Math.round(pct)}% of received
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.label}
                        sx={{ bgcolor: s.bg, color: s.color, fontWeight: 500, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Panel>
    </Stack>
  );
};

export default HospitalitySatisfactionChart;
